import { Alert, Flex, Spin } from 'antd';
import { AdminContext } from 'constants/contexts';
import useEventEffect from 'hooks/useEvent';
import useFetchedData from 'hooks/useFetchedData';
import React, { useContext, useEffect, useState } from 'react';
import { EventEmitter } from 'utils/event';
import IngredientDrawer from './IngredientDrawer';
import List from './List';
import Toolbar from './Toolbar';

const DEFAULT_PAGE_SIZE = 25;

function Ingredients() {
  const { organizationId } = useContext(AdminContext);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingIngredient, setEditingIngredient] = useState(null);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, categoryFilter]);

  const ingredientsParams = organizationId ? {
    url: `v1/organizations/${organizationId}/ingredients/`,
    query: {
      search: searchTerm || undefined,
      category: categoryFilter !== 'all' ? categoryFilter : undefined,
      page,
      page_size: pageSize,
    },
  } : null;

  const {
    data: ingredientData,
    loading,
    mutate: refreshIngredients,
    error,
  } = useFetchedData(
    ingredientsParams,
    (data = {}) => {
      const list = (
        data?.data?.results
        || data?.results
        || data?.data
        || data
      );
      const normalizedList = Array.isArray(list) ? list : [];
      const totalCount = data?.data?.count ?? data?.count ?? normalizedList.length;
      return {
        ingredients: normalizedList.map((ingredient) => ({ ...ingredient, key: ingredient.id })),
        totalCount,
      };
    },
  );
  const ingredients = ingredientData?.ingredients || [];
  const totalCount = ingredientData?.totalCount ?? ingredients.length;

  useEventEffect(() => {
    refreshIngredients();
  }, 'refresh_ingredients');

  if (loading && !searchTerm && categoryFilter === 'all' && page === 1) {
    return (
      <Flex align="center" justify="center" className="h-[50vh]">
        <Spin size="large" />
      </Flex>
    );
  }

  return (
    <Flex vertical gap={12}>
      <Toolbar
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        categoryFilter={categoryFilter}
        setCategoryFilter={setCategoryFilter}
        loading={loading}
        totalCount={totalCount}
        onCreate={() => {
          setEditingIngredient(null);
          setDrawerOpen(true);
        }}
      />

      {error && (
        <Alert
          type="warning"
          showIcon
          message="Unable to load ingredients for this organization."
        />
      )}

      <List
        organizationId={organizationId}
        ingredients={ingredients}
        loading={loading}
        searchTerm={searchTerm}
        categoryFilter={categoryFilter}
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        onCreate={() => {
          setEditingIngredient(null);
          setDrawerOpen(true);
        }}
        onEdit={(ingredient) => {
          setEditingIngredient(ingredient);
          setDrawerOpen(true);
        }}
      />

      <IngredientDrawer
        open={drawerOpen}
        ingredient={editingIngredient}
        organizationId={organizationId}
        onClose={() => {
          setDrawerOpen(false);
          setEditingIngredient(null);
        }}
      />
    </Flex>
  );
}

export default Ingredients;
